import {
  ALL_TRANSLATION_KEYS,
  MAX_TRANSLATION_VALUE_LENGTH,
  TRUST_BAR_IDS,
  FEATURE_IDS,
  STEP_IDS,
  ROLE_IDS,
  ABOUT_FEATURE_IDS,
  SOLUTIONS_SMART_IDS,
  SOLUTIONS_ALT_IDS,
  DEVICE_IDS,
  CONTACT_INFO_IDS,
  CONTACT_FIELD_IDS,
} from "../constants";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidUrl(value) {
  if (!value || typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed.startsWith("/") || /^https?:\/\//i.test(trimmed);
}

function prefixErrors(prefix, errors) {
  const result = {};
  Object.entries(errors).forEach(([key, message]) => {
    result[`${prefix}.${key}`] = message;
  });
  return result;
}

export function validateTranslations(values) {
  const errors = {};

  ALL_TRANSLATION_KEYS.forEach((key) => {
    const raw = values[key];
    const value = typeof raw === "string" ? raw.trim() : "";
    const isOptionalAltKey = /\.alt$/.test(key) || /\.imageAlt$/.test(key);

    if (!value) {
      if (isOptionalAltKey) return;
      errors[key] = "Required.";
      return;
    }

    if (value.length > MAX_TRANSLATION_VALUE_LENGTH) {
      errors[key] = `Max ${MAX_TRANSLATION_VALUE_LENGTH} chars.`;
    }

    if (key === "footer.contact.email" && !EMAIL_REGEX.test(value)) {
      errors[key] = "Invalid email.";
    }
  });

  return errors;
}

export function validateTranslationsBilingual(valuesEn, valuesEs) {
  return {
    ...prefixErrors("en", validateTranslations(valuesEn)),
    ...prefixErrors("es", validateTranslations(valuesEs)),
  };
}

function requireText(errors, path, value, maxLength) {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) {
    errors[path] = "Required.";
    return;
  }
  if (maxLength && trimmed.length > maxLength) {
    errors[path] = `Max ${maxLength} chars.`;
  }
}

function requireImage(errors, path, image) {
  const src = image && image.src ? image.src.trim() : "";
  const alt = image && image.alt !== undefined ? String(image.alt).trim() : "";
  if (!src) {
    errors[`${path}.src`] = "Image URL required.";
  }
  if (alt.length > 200) {
    errors[`${path}.alt`] = "Max 200 chars.";
  }
}

export function validateHomePage(home) {
  const errors = {};
  if (!home) {
    errors._form = "Content missing.";
    return errors;
  }

  const hero = home.hero || {};
  requireText(errors, "hero.headline.prefix", hero.headline && hero.headline.prefix, 80);
  requireText(errors, "hero.headline.highlight", hero.headline && hero.headline.highlight, 120);
  requireText(errors, "hero.headline.rest", hero.headline && hero.headline.rest, 120);
  requireText(errors, "hero.description", hero.description, 600);

  const bg = hero.backgroundImage ? String(hero.backgroundImage).trim() : "";
  if (!bg) errors["hero.backgroundImage"] = "Required.";

  requireImage(errors, "hero.heroImage", hero.heroImage);
  requireText(errors, "hero.cta.label", hero.cta && hero.cta.label, 40);
  const ctaHref = hero.cta && hero.cta.href ? hero.cta.href.trim() : "";
  if (!ctaHref) errors["hero.cta.href"] = "Required.";
  else if (!isValidUrl(ctaHref)) errors["hero.cta.href"] = "Use / or https://";

  const trustItems = (home.trustBar && home.trustBar.items) || [];
  if (trustItems.length !== 5) errors["trustBar.items"] = "Need 5 items.";
  trustItems.forEach((item, index) => {
    if (!TRUST_BAR_IDS.includes(item.id)) errors[`trustBar.items.${index}.id`] = "Invalid id.";
    requireText(errors, `trustBar.items.${index}.title`, item.title, 200);
    requireImage(errors, `trustBar.items.${index}.image`, item.image);
  });

  const challenge = (home.careChallenge && home.careChallenge.challenge) || {};
  requireText(errors, "careChallenge.challenge.title", challenge.title, 200);
  const challengeParagraphs = challenge.paragraphs || [];
  if (!challengeParagraphs.some((p) => String(p).trim())) {
    errors["careChallenge.challenge.paragraphs"] = "Need a paragraph.";
  }

  requireText(errors, "careChallenge.bridgeText", home.careChallenge && home.careChallenge.bridgeText, 150);

  const platform = (home.careChallenge && home.careChallenge.platform) || {};
  requireText(errors, "careChallenge.platform.title", platform.title, 200);
  const platformParagraphs = platform.paragraphs || [];
  if (!platformParagraphs.some((p) => String(p).trim())) {
    errors["careChallenge.platform.paragraphs"] = "Need a paragraph.";
  }

  const featureItems = (home.features && home.features.items) || [];
  if (featureItems.length !== 9) errors["features.items"] = "Need 9 items.";
  requireText(errors, "features.sectionTitle", home.features && home.features.sectionTitle, 200);
  requireImage(errors, "features.sectionIcon", home.features && home.features.sectionIcon);
  featureItems.forEach((item, index) => {
    if (!FEATURE_IDS.includes(item.id)) errors[`features.items.${index}.id`] = "Invalid id.";
    requireText(errors, `features.items.${index}.title`, item.title, 200);
    requireText(errors, `features.items.${index}.description`, item.description, 1000);
    requireImage(errors, `features.items.${index}.image`, item.image);
  });

  const howItWorks = home.howItWorks || {};
  requireText(errors, "howItWorks.sectionTitle", howItWorks.sectionTitle, 200);
  requireImage(errors, "howItWorks.sectionIcon", howItWorks.sectionIcon);
  requireImage(errors, "howItWorks.image", howItWorks.image);
  const steps = howItWorks.steps || [];
  if (steps.length !== 4) errors["howItWorks.steps"] = "Need 4 steps.";
  steps.forEach((step, index) => {
    if (!STEP_IDS.includes(step.id)) errors[`howItWorks.steps.${index}.id`] = "Invalid id.";
    requireText(errors, `howItWorks.steps.${index}.title`, step.title, 200);
    requireText(errors, `howItWorks.steps.${index}.description`, step.description, 600);
  });

  const roles = (home.roleExperience && home.roleExperience.roles) || [];
  requireText(errors, "roleExperience.sectionTitle", home.roleExperience && home.roleExperience.sectionTitle, 200);
  requireImage(
    errors,
    "roleExperience.sectionIcon",
    home.roleExperience && home.roleExperience.sectionIcon
  );
  if (roles.length !== 4) errors["roleExperience.roles"] = "Need 4 roles.";
  roles.forEach((role, index) => {
    if (!ROLE_IDS.includes(role.id)) errors[`roleExperience.roles.${index}.id`] = "Invalid id.";
    requireText(errors, `roleExperience.roles.${index}.title`, role.title, 200);
    requireImage(errors, `roleExperience.roles.${index}.image`, role.image);
    if (!(role.points || []).some((p) => String(p).trim())) {
      errors[`roleExperience.roles.${index}.points`] = "Need bullets.";
    }
  });

  const security = home.security || {};
  requireText(errors, "security.title", security.title, 200);
  requireText(errors, "security.description", security.description, 1000);
  requireImage(errors, "security.image", security.image);
  const badges = security.badges || [];
  if (badges.length !== 6) errors["security.badges"] = "Need 6 badges.";
  badges.forEach((badge, index) => requireText(errors, `security.badges.${index}`, badge, 200));

  const ctaBlock = security.ctaBlock || {};
  requireText(errors, "security.ctaBlock.title", ctaBlock.title, 200);
  requireText(errors, "security.ctaBlock.description", ctaBlock.description, 600);
  requireText(errors, "security.ctaBlock.cta.label", ctaBlock.cta && ctaBlock.cta.label, 40);
  const blockHref = ctaBlock.cta && ctaBlock.cta.href ? ctaBlock.cta.href.trim() : "";
  if (!blockHref) errors["security.ctaBlock.cta.href"] = "Required.";
  else if (!isValidUrl(blockHref)) errors["security.ctaBlock.cta.href"] = "Use / or https://";
  requireImage(errors, "security.ctaBlock.image", ctaBlock.image);

  return errors;
}

export function validateHomePageBilingual(homeEn, homeEs) {
  return {
    ...prefixErrors("en", validateHomePage(homeEn)),
    ...prefixErrors("es", validateHomePage(homeEs)),
  };
}

function validateMarketingHero(errors, prefix, hero) {
  const h = hero || {};
  requireText(errors, `${prefix}.backgroundImage`, h.backgroundImage);
  requireText(errors, `${prefix}.headline`, h.headline, 200);
  requireText(errors, `${prefix}.description`, h.description, 800);
  if (h.cta) {
    requireText(errors, `${prefix}.cta.label`, h.cta.label, 40);
    const href = h.cta.href ? h.cta.href.trim() : "";
    if (!href) errors[`${prefix}.cta.href`] = "Required.";
    else if (!isValidUrl(href)) errors[`${prefix}.cta.href`] = "Use / or https://";
  }
}

function validateProgramCta(errors, prefix, block) {
  const cta = block || {};
  requireText(errors, `${prefix}.title`, cta.title, 200);
  requireText(errors, `${prefix}.description`, cta.description, 600);
  requireText(errors, `${prefix}.cta.label`, cta.cta && cta.cta.label, 40);
  const href = cta.cta && cta.cta.href ? cta.cta.href.trim() : "";
  if (!href) errors[`${prefix}.cta.href`] = "Required.";
  else if (!isValidUrl(href)) errors[`${prefix}.cta.href`] = "Use / or https://";
  requireImage(errors, `${prefix}.image`, cta.image);
}

export function validateAboutPage(about) {
  const errors = {};
  if (!about) {
    errors._form = "Content missing.";
    return errors;
  }
  validateMarketingHero(errors, "hero", about.hero);
  const features = about.features || [];
  if (features.length !== 3) errors.features = "Need 3 feature blocks.";
  features.forEach((feature, index) => {
    if (!ABOUT_FEATURE_IDS.includes(feature.id)) errors[`features.${index}.id`] = "Invalid id.";
    requireText(errors, `features.${index}.title`, feature.title, 200);
    if (!(feature.paragraphs || []).some((p) => String(p).trim())) {
      errors[`features.${index}.paragraphs`] = "Need a paragraph.";
    }
    requireImage(errors, `features.${index}.image`, feature.image);
  });
  validateProgramCta(errors, "programCta", about.programCta);
  return errors;
}

export function validateAboutPageBilingual(aboutEn, aboutEs) {
  return {
    ...prefixErrors("en", validateAboutPage(aboutEn)),
    ...prefixErrors("es", validateAboutPage(aboutEs)),
  };
}

export function validateSolutionsPage(solutions) {
  const errors = {};
  if (!solutions) {
    errors._form = "Content missing.";
    return errors;
  }
  validateMarketingHero(errors, "hero", solutions.hero);
  const smart = solutions.smartSolutions || {};
  requireText(errors, "smartSolutions.sectionTitle", smart.sectionTitle, 200);
  requireImage(errors, "smartSolutions.sectionIcon", smart.sectionIcon);
  const smartItems = smart.items || [];
  if (smartItems.length !== 4) errors["smartSolutions.items"] = "Need 4 items.";
  smartItems.forEach((item, index) => {
    if (!SOLUTIONS_SMART_IDS.includes(item.id)) errors[`smartSolutions.items.${index}.id`] = "Invalid id.";
    requireText(errors, `smartSolutions.items.${index}.title`, item.title, 200);
    requireText(errors, `smartSolutions.items.${index}.description`, item.description, 600);
    requireImage(errors, `smartSolutions.items.${index}.icon`, item.icon);
  });
  const alt = solutions.alternatingFeatures || [];
  if (alt.length !== 3) errors.alternatingFeatures = "Need 3 blocks.";
  alt.forEach((feature, index) => {
    if (!SOLUTIONS_ALT_IDS.includes(feature.id)) errors[`alternatingFeatures.${index}.id`] = "Invalid id.";
    requireText(errors, `alternatingFeatures.${index}.title`, feature.title, 200);
    if (!(feature.paragraphs || []).some((p) => String(p).trim())) {
      errors[`alternatingFeatures.${index}.paragraphs`] = "Need a paragraph.";
    }
    requireImage(errors, `alternatingFeatures.${index}.image`, feature.image);
  });
  const devices = solutions.integratedDevices || {};
  requireText(errors, "integratedDevices.sectionTitle", devices.sectionTitle, 200);
  requireText(errors, "integratedDevices.description", devices.description, 800);
  const products = devices.products || [];
  if (products.length !== 4) errors["integratedDevices.products"] = "Need 4 products.";
  products.forEach((product, index) => {
    if (!DEVICE_IDS.includes(product.id)) errors[`integratedDevices.products.${index}.id`] = "Invalid id.";
    requireText(errors, `integratedDevices.products.${index}.title`, product.title, 200);
    requireText(errors, `integratedDevices.products.${index}.description`, product.description, 1000);
    requireImage(errors, `integratedDevices.products.${index}.image`, product.image);
  });
  validateProgramCta(errors, "programCta", solutions.programCta);
  return errors;
}

export function validateSolutionsPageBilingual(solutionsEn, solutionsEs) {
  return {
    ...prefixErrors("en", validateSolutionsPage(solutionsEn)),
    ...prefixErrors("es", validateSolutionsPage(solutionsEs)),
  };
}

export function validateContactPage(contact) {
  const errors = {};
  if (!contact) {
    errors._form = "Content missing.";
    return errors;
  }
  validateMarketingHero(errors, "hero", contact.hero);
  const cards = contact.infoCards || [];
  if (cards.length !== 3) errors.infoCards = "Need 3 cards.";
  cards.forEach((card, index) => {
    if (!CONTACT_INFO_IDS.includes(card.id)) errors[`infoCards.${index}.id`] = "Invalid id.";
    requireText(errors, `infoCards.${index}.title`, card.title, 80);
    requireText(errors, `infoCards.${index}.value`, card.value, 300);
    requireText(errors, `infoCards.${index}.icon`, card.icon);
  });
  const msg = contact.messageSection || {};
  requireText(errors, "messageSection.title", msg.title, 200);
  requireText(errors, "messageSection.description", msg.description, 400);
  requireImage(errors, "messageSection.image", msg.image);
  requireText(errors, "messageSection.form.submitLabel", msg.form && msg.form.submitLabel, 40);
  const fields = (msg.form && msg.form.fields) || [];
  if (fields.length !== 5) errors["messageSection.form.fields"] = "Need 5 fields.";
  fields.forEach((field, index) => {
    if (!CONTACT_FIELD_IDS.includes(field.id)) errors[`messageSection.form.fields.${index}.id`] = "Invalid id.";
    requireText(errors, `messageSection.form.fields.${index}.label`, field.label, 80);
    requireText(errors, `messageSection.form.fields.${index}.placeholder`, field.placeholder, 80);
  });
  return errors;
}

export function validateContactPageBilingual(contactEn, contactEs) {
  return {
    ...prefixErrors("en", validateContactPage(contactEn)),
    ...prefixErrors("es", validateContactPage(contactEs)),
  };
}

export function getFirstErrorSection(errors, sectionPrefixMap) {
  const keys = Object.keys(errors);
  if (!keys.length) return null;
  for (let i = 0; i < sectionPrefixMap.length; i += 1) {
    const { id, prefixes } = sectionPrefixMap[i];
    if (keys.some((key) => {
      const normalized = key.replace(/^(en|es)\./, "");
      return prefixes.some((prefix) => normalized.startsWith(prefix));
    })) {
      return id;
    }
  }
  return sectionPrefixMap[0] && sectionPrefixMap[0].id;
}

export function formatValidationList(errors) {
  return Object.entries(errors).map(([field, message]) => ({ field, message }));
}

export function getError(path, errors, lang) {
  return errors[`${lang}.${path}`];
}
